/**
 * ENVIRONMENT: PRODUCTION
 * MODULE: Digital Gift Voucher Catalog & Margin Management (B2C)
 * PATH: src/routes/gift_vouchers/GiftVoucherList.js
 */

import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Table,
  Space,
  Row,
  Col,
  Card,
  Input,
  Select,
  Button,
  InputNumber,
  Tag,
  Tooltip,
  Typography,
  message,
} from "antd";
import {
  SearchOutlined,
  ReloadOutlined,
  CopyOutlined,
  GiftOutlined,
  CheckCircleFilled,
  CloseCircleFilled,
  CloudSyncOutlined,
} from "@ant-design/icons";
import _ from "lodash";
import { parseQueryParams, stringifyQueryParams } from "utils/url";
import { giftvoucherlist } from "requests/statistic";
import api from "utils/api";
import "./GiftVoucherList.css";

const { Text, Title } = Typography;
const { Option } = Select;

function GiftVoucherList({ onSyncCatalog, syncLoading }) {
  const [isTableLoading, setIsTableLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(
    Number(process.env.REACT_APP_RECORDS_PER_PAGE) || 10
  );
  const [allRecords, setAllRecords] = useState([]);
  const [statusFilter, setStatusFilter] = useState("");
  const [searchTerm, setSearchTerm] = useState("");

  const location = useLocation();
  const navigate = useNavigate();

  // Load Full Catalog Records
  const getData = useCallback(async (query) => {
    try {
      setIsTableLoading(true);
      const activeQuery = query || parseQueryParams(location);
      const response = await giftvoucherlist(activeQuery);

      if (response && response.data) {
        const resData = response.data;
        const items = Array.isArray(resData)
          ? resData
          : Array.isArray(resData.data)
          ? resData.data
          : [];

        setAllRecords(items);
      } else {
        setAllRecords([]);
      }
    } catch (err) {
      message.error(err?.message || "Failed to load gift voucher catalog.");
      setAllRecords([]);
    } finally {
      setIsTableLoading(false);
    }
  }, [location]);

  // Sync initial URL query parameters on mount
  useEffect(() => {
    const query = parseQueryParams(location);
    if (query.status) setStatusFilter(query.status);
    if (query.keyword) setSearchTerm(query.keyword);
    if (query.page) setPage(Number(query.page));
    if (query.per_page) setPerPage(Number(query.per_page));
    getData(query);
  }, []); // Run once on initial mount

  // Sync URL query when search or filter changes
  const updateUrlQuery = useCallback(
    (newKeyword, newStatus, newPage, newPerPage) => {
      let query = parseQueryParams(location);
      query = {
        ...query,
        page: newPage || 1,
        per_page: newPerPage || perPage,
      };

      if (newKeyword && newKeyword.trim()) {
        query.keyword = newKeyword.trim();
      } else {
        delete query.keyword;
      }

      if (newStatus) {
        query.status = newStatus;
      } else {
        delete query.status;
      }

      navigate({
        pathname: location.pathname,
        search: stringifyQueryParams(query),
      });
    },
    [location, navigate, perPage]
  );

  const debouncedSyncUrl = useRef(
    _.debounce((val, status) => {
      updateUrlQuery(val, status, 1, perPage);
    }, 450)
  ).current;

  // Real-time Case-Insensitive Filter (Brand Title, SKU / Identifier, Category)
  const filteredRecords = useMemo(() => {
    let result = allRecords;

    if (statusFilter) {
      result = result.filter(
        (item) => (item.status || "").toUpperCase() === statusFilter.toUpperCase()
      );
    }

    if (searchTerm && searchTerm.trim()) {
      const lower = searchTerm.trim().toLowerCase();
      result = result.filter((item) => {
        const titleMatch = (item.title || "").toLowerCase().includes(lower);
        const skuMatch = String(item.id || item._id || "").toLowerCase().includes(lower);
        const catMatch = Array.isArray(item.category)
          ? item.category.some((c) => String(c).toLowerCase().includes(lower))
          : String(item.category || "").toLowerCase().includes(lower);
        return titleMatch || skuMatch || catMatch;
      });
    }

    return result;
  }, [allRecords, statusFilter, searchTerm]);

  // Search Input Change Handler (Live Search)
  const handleSearchChange = (e) => {
    const val = e.target.value;
    setSearchTerm(val);
    setPage(1); // Reset to page 1 on new search
    debouncedSyncUrl(val, statusFilter);
  };

  // Status Filter Change
  const handleStatusChange = (val) => {
    const nextStatus = val || "";
    setStatusFilter(nextStatus);
    setPage(1);
    updateUrlQuery(searchTerm, nextStatus, 1, perPage);
  };

  // Table Pagination Navigation
  const onChangeTable = (pagination) => {
    const nextPage = pagination.current || 1;
    const nextPageSize = pagination.pageSize || 10;

    setPage(nextPage);
    setPerPage(nextPageSize);
    updateUrlQuery(searchTerm, statusFilter, nextPage, nextPageSize);
  };

  // Debounced Discount Update (Direct API call + local memory sync)
  const onUpdateDiscount = useRef(
    _.debounce(async (voucherId, discountValue) => {
      if (
        discountValue === null ||
        discountValue === undefined ||
        discountValue === "" ||
        isNaN(Number(discountValue))
      ) {
        return;
      }

      const numericDiscount = Number(discountValue);

      try {
        const res = await api.post(
          `gift-voucher-discount-update/${encodeURIComponent(voucherId)}`,
          { discount: numericDiscount }
        );

        const resBody = res?.data || res;
        if (resBody && (resBody.status === 1 || resBody.status === "1")) {
          message.success("Customer discount updated successfully");
          setAllRecords((prev) =>
            prev.map((item) =>
              item.id === voucherId || item._id === voucherId
                ? { ...item, discount: numericDiscount }
                : item
            )
          );
        } else {
          message.error(resBody?.message || "Failed to update voucher discount.");
        }
      } catch (err) {
        message.error(
          err?.response?.data?.message ||
            err?.message ||
            "Failed to update voucher discount."
        );
      }
    }, 700)
  ).current;

  // Copy helper
  const copyToClipboard = (text, label) => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    message.success(`${label} copied to clipboard`);
  };

  // Avatar color generator
  const getAvatarColor = (name = "") => {
    const colors = ["#0284c7", "#059669", "#7c3aed", "#d97706", "#dc2626", "#4f46e5"];
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    return colors[Math.abs(hash) % colors.length];
  };

  const handleRefresh = () => {
    setSearchTerm("");
    setStatusFilter("");
    setPage(1);
    navigate({
      pathname: location.pathname,
      search: stringifyQueryParams({ page: 1, per_page: perPage }),
    });
    getData({ page: 1, per_page: perPage });
  };

  const columns = [
    {
      title: "SR",
      width: 60,
      align: "center",
      render: (_, __, index) => (
        <span className="voucher-sr">{(page - 1) * perPage + index + 1}</span>
      ),
    },
    {
      title: "Voucher SKU / Identifier",
      width: 220,
      render: (_, record) => (
        <div className="sku-cell">
          <span className="sku-code">{record.id}</span>
          <Tooltip title="Copy Voucher SKU">
            <CopyOutlined
              className="copy-sku-icon"
              onClick={() => copyToClipboard(record.id, "Voucher SKU")}
            />
          </Tooltip>
        </div>
      ),
    },
    {
      title: "Brand & Catalog Item",
      width: 240,
      render: (_, record) => {
        const brandTitle = record.title || "Brand Voucher";
        const initial = brandTitle.charAt(0).toUpperCase();
        return (
          <div className="brand-item-cell">
            <div
              className="brand-avatar-chip"
              style={{ backgroundColor: getAvatarColor(brandTitle) }}
            >
              {initial}
            </div>
            <div className="brand-meta">
              <span className="brand-name">{brandTitle}</span>
              <span className="brand-category">Instant E-Voucher</span>
            </div>
          </div>
        );
      },
    },
    {
      title: "API Discount",
      width: 140,
      align: "center",
      render: (_, record) => {
        const apiDisc = Number(record.discountPercentage) || 0;
        return (
          <Tag color="cyan" className="api-discount-tag">
            {apiDisc > 0 ? `${apiDisc}% Base` : "0.0%"}
          </Tag>
        );
      },
    },
    {
      title: "Customer Discount (Margin)",
      width: 200,
      render: (_, record) => {
        const apiDisc = Number(record.discountPercentage) || 0;
        const targetId = String(record.id || record._id || "");
        return (
          <div className="margin-editor-cell">
            <InputNumber
              key={`${targetId}_${record.discount}`}
              size="middle"
              min={0}
              max={100}
              step={0.1}
              precision={2}
              formatter={(val) => (val !== undefined && val !== "" ? `${val}%` : "")}
              parser={(val) => (val ? val.replace("%", "") : "")}
              defaultValue={Number(record.discount) || 0}
              onChange={(value) => onUpdateDiscount(targetId, value)}
              className="custom-discount-stepper"
            />
            {apiDisc > 0 && (
              <span className="spread-label">Provider: {apiDisc}%</span>
            )}
          </div>
        );
      },
    },
    {
      title: "Status",
      width: 130,
      align: "center",
      render: (_, record) => {
        const isLive = (record.status || "").toUpperCase() === "ACTIVE";
        return (
          <Tag
            icon={isLive ? <CheckCircleFilled /> : <CloseCircleFilled />}
            color={isLive ? "success" : "default"}
            className="voucher-status-tag"
          >
            {isLive ? "ACTIVE" : "INACTIVE"}
          </Tag>
        );
      },
    },
  ];

  return (
    <div className="gift-voucher-catalog-container">
      {/* Top Header Card */}
      <Card className="voucher-header-card" bordered={false}>
        <Row justify="space-between" align="middle" gutter={[16, 16]}>
          <Col xs={24} lg={8}>
            <div className="voucher-title-wrap">
              <GiftOutlined className="header-gift-icon" />
              <div>
                <Title level={4} className="voucher-main-heading">
                  Gift Voucher Catalog
                </Title>
                <Text type="secondary" className="voucher-subtext">
                  Configure consumer margins, active brand distribution &amp; provider sync
                </Text>
              </div>
            </div>
          </Col>

          <Col xs={24} lg={16} className="voucher-controls-wrap">
            <Space wrap size="middle" className="controls-flex">
              {/* Status Filter */}
              <Select
                placeholder="All Catalog States"
                value={statusFilter || undefined}
                onChange={handleStatusChange}
                allowClear
                className="voucher-status-select"
              >
                <Option value="ACTIVE">Active Only</Option>
                <Option value="INACTIVE">Inactive Only</Option>
              </Select>

              {/* Brand Search Input */}
              <Input
                placeholder="Search Brand (Amazon, Zepto, etc.) or SKU..."
                prefix={<SearchOutlined style={{ color: "#94a3b8" }} />}
                value={searchTerm}
                onChange={handleSearchChange}
                allowClear
                className="voucher-search-input"
              />

              {/* Provider Sync Catalog Button */}
              {onSyncCatalog && (
                <Button
                  icon={<CloudSyncOutlined />}
                  onClick={onSyncCatalog}
                  loading={syncLoading}
                  className="voucher-sync-btn"
                >
                  Sync Catalog
                </Button>
              )}

              {/* Refresh Trigger */}
              <Button
                icon={<ReloadOutlined />}
                onClick={handleRefresh}
                className="voucher-refresh-btn"
              />
            </Space>
          </Col>
        </Row>
      </Card>

      {/* Main Catalog Table */}
      <Card className="voucher-table-card" bordered={false}>
        <Table
          loading={isTableLoading}
          dataSource={filteredRecords}
          columns={columns}
          onChange={onChangeTable}
          rowKey={(record) => String(record.id || record._id)}
          pagination={{
            current: page,
            pageSize: perPage,
            total: filteredRecords.length,
            showSizeChanger: true,
            pageSizeOptions: ["10", "25", "50", "100"],
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} of ${total} records`,
          }}
          scroll={{ x: 1000 }}
        />
      </Card>
    </div>
  );
}

export default GiftVoucherList;